/**
 * Custom GenericTableComponent - Override Component
 * Transforms customer sales data by pivoting years into columns
 */
import {
  Component,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { map, tap, takeUntil, debounceTime, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { GenericTableComponent as ParentGenericTableComponent } from '@shared-lib/chart-components/generic-table/generic-table';
import { PaginatorComponent } from '@shared-lib/global-components/paginator/paginator.component';
import { CustomConfigService } from '../../shared-services/custom-config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { downloadAsExcel } from '@shared-lib/utils/export.utils';

@Component({
  selector: 'app-generic-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    PaginatorComponent,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './generic-table.html',
  styleUrls: ['./generic-table.scss'],
})
export class GenericTableComponent
  extends ParentGenericTableComponent
  implements AfterViewInit, OnDestroy {
  public readonly noDataText = 'No Data avaliable try changing filters';
  @Output() toggleFullscreen = new EventEmitter<void>();
  public isFullScreen = false;
  private stickyColumns: string[] = [];
  public sortColumn: string | null = null;
  public sortDirection: 'asc' | 'desc' = 'desc';
  public sortType: 'column' | 'group' = 'column';
  public sortGroupColumns: string[] = [];

  private refreshStickyColumns(): void {
    const configuredStickyColumns = (this.data as any)?.stickyColumns;
    if (Array.isArray(configuredStickyColumns)) {
      const configuredSet = new Set(
        configuredStickyColumns.map((col: any) => String(col)),
      );
      this.stickyColumns = (this.displayedColumns || []).filter((col) =>
        configuredSet.has(col),
      );
      return;
    }

    if (
      ((this.data as any)?.stickyFirstColumn === true || this.isQuarterlyEnabled()) &&
      Array.isArray(this.displayedColumns) &&
      this.displayedColumns.length > 0
    ) {
      if (
        this.displayedColumns[0] === 'customer_name' &&
        this.displayedColumns[1] === 'channel_type'
      ) {
        this.stickyColumns = ['customer_name', 'channel_type'];
      } else {
        this.stickyColumns = [this.displayedColumns[0]];
      }
      return;
    }

    this.stickyColumns = [];
  }

  public isSpacerGroup(group: any): boolean {
    if (!group) return false;
    const cols: string[] = group.columns || [];
    // A group is a spacer only if all its columns are currently marked as sticky.
    return cols.length > 0 && cols.every((c) => this.isStickyColumn(c));
  }

  /** Returns the total pixel width of a group (sum of its columns' widths) */
  public override getGroupWidthPx(group: any): number {
    const cols: string[] = group?.columns || [];
    if (!cols.length) return this.DEFAULT_COL_WIDTH;
    return cols.reduce(
      (sum, col) => sum + (this.columnWidths[col] ?? this.DEFAULT_COL_WIDTH),
      0,
    );
  }

  public isGroupSticky(group: any): boolean {
    // The spacer group (no label) covers identifier columns and must be frozen
    return this.isSpacerGroup(group);
  }

  public getGroupLeft(group: any): number {
    if (!this.isSpacerGroup(group)) return 0;
    const index = (this.headerGroups || []).indexOf(group);
    if (index <= 0) return 0;

    let left = 0;
    for (let i = 0; i < index; i++) {
      const prev = this.headerGroups[i];
      if (this.isSpacerGroup(prev)) {
        left += this.getGroupWidthPx(prev);
      }
    }
    return left;
  }

  public getGroupZIndex(group: any): number | null {
    const cols: string[] = group?.columns || [];
    if (cols.length === 0) return null;
    const index = this.stickyColumns.indexOf(cols[0]);
    if (index < 0) return null;
    // Give group headers the maximum sticky priority (like 300) so they obliterate all row/header overlaps.
    return 300 + (this.stickyColumns.length - index);
  }

  public override isSingleQuarterYearGroup(
    group: { label?: string; columns?: string[] } | null | undefined,
  ): boolean {
    if (!this.isQuarterlyEnabled()) return false;
    const label = String(group?.label || '');
    const cols = group?.columns || [];
    return /^\d{4}$/.test(label) && cols.length === 1;
  }

  public override onHeaderMouseDown(event: MouseEvent, column: string): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const distanceFromRightEdge = rect.right - event.clientX;
    if (distanceFromRightEdge <= 16) {
      this.onResizeStart(event, column);
    }
  }

  public override isQuarterlyIdentifierColumn(column: string): boolean {
    return (
      this.isQuarterlyEnabled() &&
      (column === 'customer_name' || column === 'channel_type')
    );
  }

  public override isQuarterlyIdentifierGroup(
    group: { columns?: string[] } | null | undefined,
  ): boolean {
    if (!this.isQuarterlyEnabled()) return false;
    const cols = group?.columns || [];
    return cols.length === 1 && this.isQuarterlyIdentifierColumn(cols[0]);
  }

  constructor(
    protected override configService: CustomConfigService,
    protected override globalFilterService: GlobalFilterService,
    protected override chartFilterHandler: ChartFilterHandlerService,
    protected override http: HttpClient,
  ) {
    super(configService, globalFilterService, chartFilterHandler, http);
  }

  public onToggleFullscreen(): void {
    this.toggleFullscreen.emit();
  }

  override ngAfterViewInit(): void {
    const widgetId = (this.data as any)?.widgetId || 'unknown';

    // console.log(
    //   '[Custom GenericTableComponent] ngAfterViewInit for widget:',
    //   widgetId,
    //   {
    //     enableQuarterlyPivot: (this.data as any)?.enableQuarterlyPivot,
    //     yearField: (this.data as any)?.yearField,
    //     quarterField: (this.data as any)?.quarterField,
    //     rawHeaderColumns: (this.data as any)?.headerColumns,
    //     rawHeaderGroups: (this.data as any)?.headerGroups,
    //   },
    // );

    // Defer loading and filter wiring to the base implementation.
    super.ngAfterViewInit();
  }

  public override handleExternalLegendFilter(event: any): void {
    super.handleExternalLegendFilter(event);

    const widgetId = (this.data as any)?.widgetId;
    if (!widgetId || !event.targetWidgetIds.includes(widgetId)) return;

    // Logic to hide/show columns based on year legends
    const isYearFilter = event.legendKey === 'year' || event.isCategoryFilter;
    if (isYearFilter) {
      this.updateColumnVisibility(event.selectedLegends);
    }
  }

  private updateColumnVisibility(selectedYears: string[]): void {
    const rawHeaderColumns = this.data?.headerColumns;
    if (!rawHeaderColumns || !Array.isArray(rawHeaderColumns)) return;

    if (!selectedYears || selectedYears.length === 0) {
      // Restore all columns from original data
      (this as any).displayedColumns = rawHeaderColumns.map((c: any) => c.key);
      this.refreshStickyColumns();
      return;
    }

    // Filter columns: keep identifiers and selected years
    // Identifiers are columns that don't look like years (non-numeric 4-digit strings)
    (this as any).displayedColumns = rawHeaderColumns
      .map((c: any) => c.key)
      .filter((colKey: string) => {
        const isYearCol = /^\d{4}$/.test(colKey);
        if (!isYearCol) return true; // Keep ECC, TOTAL, Customer Name, etc.
        return selectedYears.includes(colKey); // Keep only matching selected years
      });

    // Refresh sticky columns since displayedColumns changed
    this.refreshStickyColumns();
  }

  /**
   * Override base clearAllSelections to also clear the stored legend filter
   * and restore all table columns. This is called by the parent's
   * loadAndRenderChart when it detects a global reset (empty filters),
   * ensuring the table fully resets its column visibility state.
   */
  protected override clearAllSelections(): void {
    super.clearAllSelections();
    // Clear the stored legend filter so processApiResponse won't re-apply it
    (this as any).activeLegendSyncFilter = null;
    // Restore all columns from config
    this.updateColumnVisibility([]);
  }

  private isQuarterlyEnabled(): boolean {
    const enabled = this.data?.enableQuarterlyPivot === true;
    const widgetId = (this.data as any)?.widgetId || 'unknown';
    // console.log(
    //   `[Custom GenericTableComponent] isQuarterlyEnabled for widget ${widgetId}:`,
    //   enabled,
    // );
    return enabled;
  }

  private hasQuarterField(customers: any[]): boolean {
    const qField = this.data?.quarterField || 'quarter';
    const has =
      Array.isArray(customers) && customers.some((r) => !!r?.[qField]);
    const widgetId = (this.data as any)?.widgetId || 'unknown';
    console.log(
      `[Custom GenericTableComponent] hasQuarterField for widget ${widgetId}:`,
      has,
      'using qField:',
      qField,
      'sample[0]:',
      customers && customers[0] ? { ...customers[0] } : null,
    );
    return has;
  }

  private pivotCustomerQuarterlyData(customers: any[]): {
    rows: any[];
    headerColumns: any[];
    headerGroups: any[];
  } {
    const yearField = this.data?.yearField || 'year';
    const qField = this.data?.quarterField || 'quarter';
    const quarterOrder: string[] = this.data?.quarterOrder || [
      'Q1',
      'Q2',
      'Q3',
      'Q4',
    ];

    // Build a map of year -> set of quarters that actually exist in the data.
    let yearQuarterMap = new Map<string, Set<string>>();
    customers.forEach((item) => {
      const yRaw = item?.[yearField];
      const qRaw = item?.[qField];
      const y = yRaw != null ? String(yRaw) : '';
      const q = qRaw != null ? String(qRaw).toUpperCase() : '';

      if (!/^\d{4}$/.test(y)) return;
      if (!q || !quarterOrder.includes(q)) return;

      if (!yearQuarterMap.has(y)) {
        yearQuarterMap.set(y, new Set<string>());
      }
      yearQuarterMap.get(y)!.add(q);
    });

    // Optional config-driven structure for years/quarters.
    // Example:
    // yearQuarterMap: {
    //   "2023": ["Q4"],
    //   "2024": ["Q1", "Q2", "Q3", "Q4"],
    //   "2025": ["Q1", "Q2", "Q3", "Q4"]
    // }
    const configuredYearQuarterMap = new Map<string, Set<string>>();
    const rawYearQuarterMap = this.data?.yearQuarterMap;
    const strictYearQuarterMap = this.data?.strictYearQuarterMap === true;

    if (rawYearQuarterMap && typeof rawYearQuarterMap === 'object') {
      Object.entries(rawYearQuarterMap).forEach(([year, quarters]) => {
        const normalizedYear = String(year);
        if (!/^\d{4}$/.test(normalizedYear)) return;
        const quarterSet = new Set<string>();
        if (Array.isArray(quarters)) {
          quarters.forEach((quarter) => {
            const normalizedQuarter = String(quarter || '').toUpperCase();
            if (quarterOrder.includes(normalizedQuarter)) {
              quarterSet.add(normalizedQuarter);
            }
          });
        }
        if (quarterSet.size > 0) {
          configuredYearQuarterMap.set(normalizedYear, quarterSet);
        }
      });
    }

    if (configuredYearQuarterMap.size > 0) {
      if (strictYearQuarterMap) {
        yearQuarterMap = configuredYearQuarterMap;
      } else {
        configuredYearQuarterMap.forEach((configuredQuarters, year) => {
          if (!yearQuarterMap.has(year)) {
            yearQuarterMap.set(year, new Set<string>());
          }
          const target = yearQuarterMap.get(year)!;
          configuredQuarters.forEach((quarter) => target.add(quarter));
        });
      }
    }

    // Get active filters from global filter service
    const currentFilters = (this as any).globalFilterService.getCurrentFilters();
    const activeYearFilters = (Array.isArray(currentFilters?.requestParams?.year)
      ? currentFilters.requestParams.year
      : [currentFilters?.requestParams?.year]
    ).filter(Boolean).map(String);
    const activeQtrFilters = (Array.isArray(currentFilters?.requestParams?.qtr)
      ? currentFilters.requestParams.qtr
      : [currentFilters?.requestParams?.qtr]
    ).filter(Boolean).map((q: string) => String(q).toUpperCase());

    // Final list of years that actually have at least one valid quarter
    let years: string[] = Array.from(yearQuarterMap.keys()).sort(
      (a, b) => Number(a) - Number(b),
    );

    // Apply active Year filter if present
    if (activeYearFilters.length > 0) {
      years = years.filter((y) => activeYearFilters.includes(y));
    }

    // Apply user-requested mapping: 2023 starts at Q4, 2026 ends at Q2
    if (yearQuarterMap.has('2023')) {
      const q2023 = yearQuarterMap.get('2023')!;
      Array.from(q2023).forEach((q) => {
        if (q !== 'Q4') q2023.delete(q);
      });
      if (q2023.size === 0) {
        yearQuarterMap.delete('2023');
        years = years.filter((y) => y !== '2023');
      }
    }

    if (yearQuarterMap.has('2026')) {
      const q2026 = yearQuarterMap.get('2026')!;
      Array.from(q2026).forEach((q) => {
        if (q !== 'Q1' && q !== 'Q2') q2026.delete(q);
      });
      if (q2026.size === 0) {
        yearQuarterMap.delete('2026');
        years = years.filter((y) => y !== '2026');
      }
    }

    const headerColsFromConfig: any[] = Array.isArray(this.data?.headerColumns)
      ? this.data.headerColumns
      : [];
    const customerLabel =
      headerColsFromConfig.find((c: any) => c?.key === 'customer_name')
        ?.label || 'CUSTOMER NAME';
    const channelLabel =
      headerColsFromConfig.find((c: any) => c?.key === 'channel_type')?.label ||
      'CHANNEL TYPE';
    const idCols = [
      { key: 'customer_name', label: customerLabel },
      { key: 'channel_type', label: channelLabel },
    ];

    // Build quarter columns per year (e.g. 2024_Q1, 2024_Q2) but
    // only for quarters that are actually present in the data.
    const qCols: any[] = [];
    years.forEach((y) => {
      // Filter quarters based on active Year+Qtr filter pairs (coordinate-aware)
      const presentQuarters = yearQuarterMap.get(y)
        ? quarterOrder.filter((q) => {
          const hasData = yearQuarterMap.get(y)!.has(q);
          if (!hasData) return false;

          // Coordinate-aware filtering: if both year and qtr are active, they must match at the same index
          if (activeYearFilters.length > 0 && activeQtrFilters.length > 0) {
            // Only match if this specific (year, quarter) pair exists in the combined filter list
            return activeYearFilters.some((ay: string, idx: number) =>
              ay === y && activeQtrFilters[idx] === q.toUpperCase()
            );
          }

          // Fallback for independent filters (or single-dimension filter)
          const matchesYear = activeYearFilters.length === 0 || activeYearFilters.includes(y);
          const matchesQtr = activeQtrFilters.length === 0 || activeQtrFilters.includes(q.toUpperCase());
          return matchesYear && matchesQtr;
        })
        : [];

      presentQuarters.forEach((q) => {
        qCols.push({ key: `${y}_${q}`, label: q }); // label is just Q1/Q2 (year is in grouped header)
      });
    });

    const totalKey = this.data?.totalKey || 'TOTAL';
    const includeTotal = this.data?.includeTotalColumn !== false;

    const headerColumns = [
      ...idCols,
      ...qCols,
      ...(includeTotal ? [{ key: totalKey, label: totalKey }] : []),
    ];

    const headerGroups: any[] = [];

    // Identifier columns will have their default widths set in initColumnWidths()

    idCols.forEach((c) => {
      headerGroups.push({
        label: c.key === 'channel_type' ? 'YEAR' : '',
        columns: [c.key],
        colspan: 1,
      });
    });

    // One group per year spanning its existing quarters
    years.forEach((y) => {
      const presentQuarters = yearQuarterMap.get(y)
        ? quarterOrder.filter((q) => {
          const hasData = yearQuarterMap.get(y)!.has(q);
          if (!hasData) return false;

          // Match exact Year-Quarter pair if both are filtered
          if (activeYearFilters.length > 0 && activeQtrFilters.length > 0) {
            return activeYearFilters.some((ay: string, idx: number) =>
              ay === y && activeQtrFilters[idx] === q.toUpperCase()
            );
          }

          const matchesYear = activeYearFilters.length === 0 || activeYearFilters.includes(y);
          const matchesQtr = activeQtrFilters.length === 0 || activeQtrFilters.includes(q.toUpperCase());
          return matchesYear && matchesQtr;
        })
        : [];
      if (presentQuarters.length > 0) {
        headerGroups.push({
          label: y,
          columns: presentQuarters.map((q) => `${y}_${q}`),
          colspan: presentQuarters.length,
        });
      }
    });

    if (includeTotal) {
      headerGroups.push({
        label: '', // Or 'Total' if we want the label there
        columns: [totalKey],
        colspan: 1,
      });
    }

    // Pivot rows
    const groupMap = new Map<string, any>();

    customers.forEach((item) => {
      const key = `${item.customer_name}||${item.channel_type}`;
      if (!groupMap.has(key)) {
        const base: any = {
          customer_name: item.customer_name,
          channel_type: item.channel_type,
        };
        // init only the quarter keys that actually exist for each year
        years.forEach((y) => {
          const presentQuarters = yearQuarterMap.get(y)
            ? quarterOrder.filter((q) => yearQuarterMap.get(y)!.has(q))
            : [];
          presentQuarters.forEach((q) => {
            base[`${y}_${q}`] = 0;
          });
        });
        if (includeTotal) base[totalKey] = 0;
        groupMap.set(key, base);
      }

      const row = groupMap.get(key);
      const y = String(item?.[yearField]);
      const q = String(item?.[qField] || '').toUpperCase();

      const colKey = `${y}_${q}`;
      if (row.hasOwnProperty(colKey)) {
        row[colKey] = (row[colKey] || 0) + (item.sumOfTotalSales || 0);
      }
    });

    const rawRows = Array.from(groupMap.values()).map((row) => {
      if (includeTotal) {
        row[totalKey] = years.reduce((sumY, y) => {
          const presentQuarters = yearQuarterMap.get(y)
            ? quarterOrder.filter((q) => yearQuarterMap.get(y)!.has(q))
            : [];
          return (
            sumY +
            presentQuarters.reduce((sumQ, q) => sumQ + (row[`${y}_${q}`] || 0), 0)
          );
        }, 0);
      }
      return row;
    });

    if (includeTotal) {
      rawRows.sort((a, b) => (b[totalKey] || 0) - (a[totalKey] || 0));
    }

    const rows = rawRows.map((row) => {
      years.forEach((y) => {
        const presentQuarters = yearQuarterMap.get(y)
          ? quarterOrder.filter((q) => {
            const hasData = yearQuarterMap.get(y)!.has(q);
            if (!hasData) return false;

            // Match exact Year-Quarter pair if both are filtered
            if (activeYearFilters.length > 0 && activeQtrFilters.length > 0) {
              return activeYearFilters.some((ay: string, idx: number) =>
                ay === y && activeQtrFilters[idx] === q.toUpperCase()
              );
            }

            const matchesYear = activeYearFilters.length === 0 || activeYearFilters.includes(y);
            const matchesQtr = activeQtrFilters.length === 0 || activeQtrFilters.includes(q.toUpperCase());
            return matchesYear && matchesQtr;
          })
          : [];
        presentQuarters.forEach((q) => {
          const colKey = `${y}_${q}`;
          row[colKey] = this.formatCurrency(Number(row[colKey]));
        });
      });

      if (includeTotal) {
        row[totalKey] = this.formatCurrency(Number(row[totalKey]));
      }

      return row;
    });

    const widgetId = (this.data as any)?.widgetId || 'unknown';
    console.log(
      `[Custom GenericTableComponent] pivotCustomerQuarterlyData for widget ${widgetId}:`,
      {
        yearField,
        qField,
        years,
        strictYearQuarterMap,
        yearQuarterMap: Array.from(yearQuarterMap.entries()).map(([y, qs]) => ({
          year: y,
          quarters: Array.from(qs.values()),
        })),
        quarterOrder,
        headerColumns,
        headerGroups,
        sampleRow: rows[0] || null,
      },
    );

    return { rows, headerColumns, headerGroups };
  }

  // Helper: find customer array anywhere in response object
  private findCustomerArray(obj: any): any[] {
    if (!obj) return [];

    if (Array.isArray(obj)) {
      if (
        obj.length > 0 &&
        obj[0].hasOwnProperty('customer_name') &&
        obj[0].hasOwnProperty('year')
      ) {
        return obj;
      }
      for (const item of obj) {
        const result = this.findCustomerArray(item);
        if (result.length > 0) return result;
      }
    }

    if (typeof obj === 'object') {
      if (obj.customer && Array.isArray(obj.customer)) {
        const customerArray = obj.customer;
        if (
          customerArray.length > 0 &&
          customerArray[0].hasOwnProperty('customer_name')
        ) {
          return customerArray;
        }
      }

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const result = this.findCustomerArray(obj[key]);
          if (result.length > 0) return result;
        }
      }
    }

    return [];
  }

  // Helper: find ecc array anywhere in response object
  private findEccArray(obj: any): any[] {
    if (!obj) return [];

    if (Array.isArray(obj)) {
      if (
        obj.length > 0 &&
        Object.prototype.hasOwnProperty.call(obj[0], 'ecc') &&
        Object.prototype.hasOwnProperty.call(obj[0], 'year')
      ) {
        return obj;
      }
      for (const item of obj) {
        const result = this.findEccArray(item);
        if (result.length > 0) return result;
      }
    }

    if (typeof obj === 'object') {
      if (obj.ecc && Array.isArray(obj.ecc) && obj.ecc.length > 0) {
        return obj.ecc;
      }

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const result = this.findEccArray(obj[key]);
          if (result.length > 0) return result;
        }
      }
    }

    return [];
  }

  // Pivot ecc response shape:
  // [{ ecc, year, value, total, isTotal }] -> [{ ecc, "2023", "2024", "2025", TOTAL }]
  private pivotEccData(rows: any[]): any[] {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const currentFilters = (this as any).globalFilterService.getCurrentFilters();
    const activeYearFilters = (Array.isArray(currentFilters?.requestParams?.year)
      ? currentFilters.requestParams.year
      : [currentFilters?.requestParams?.year]
    ).filter(Boolean).map(String);

    let years: string[] = this.getYearColumnsFromConfig();
    if (!years.length) {
      years = Array.from(
        new Set(
          rows
            .map((r: any) => String(r?.year ?? ''))
            .filter((y: string) => /^\d{4}$/.test(y)),
        ),
      ).sort((a, b) => Number(a) - Number(b));
    }

    if (activeYearFilters.length > 0) {
      years = years.filter((y) => activeYearFilters.includes(y));
    }

    const totalKey = this.data?.totalKey || 'TOTAL';
    const map = new Map<string, any>();

    rows.forEach((item: any) => {
      const eccName = String(item?.ecc ?? '').trim();
      if (!eccName) return;

      if (!map.has(eccName)) {
        const base: any = { ecc: eccName };
        years.forEach((y) => {
          base[y] = '-';
        });
        base[totalKey] = '-';
        map.set(eccName, base);
      }

      const row = map.get(eccName);
      const yearStr = String(item?.year ?? '');
      const valueNum = Number(item?.value ?? 0);
      const totalNum = Number(item?.total ?? 0);

      if (item?.isTotal === true || yearStr.toLowerCase() === 'total') {
        row[totalKey] = this.formatCurrency(totalNum || valueNum);
        return;
      }

      if (years.includes(yearStr)) {
        row[yearStr] = this.formatCurrency(valueNum);
      }
    });

    return Array.from(map.values());
  }

  // Helper: pivot customer data (group by customer_name + channel_type)
  private pivotCustomerData(customers: any[]): any[] {
    if (!customers || !Array.isArray(customers)) return [];

    const currentFilters = (this as any).globalFilterService.getCurrentFilters();
    const activeYearFilters = (Array.isArray(currentFilters?.requestParams?.year)
      ? currentFilters.requestParams.year
      : [currentFilters?.requestParams?.year]
    ).filter(Boolean).map(String);

    // Determine year columns from widget JSON if provided, otherwise infer from data
    let YEARS: string[] = this.getYearColumnsFromConfig();
    if (!YEARS || YEARS.length === 0) {
      YEARS = Array.from(new Set(customers.map((c: any) => String(c.year))))
        .filter((y: string) => /^\d{4}$/.test(y))
        .sort();
    }

    if (activeYearFilters.length > 0) {
      YEARS = YEARS.filter((y) => activeYearFilters.includes(y));
    }

    const totalKey = (this.data && this.data.totalKey) || 'TOTAL';
    const groupMap = new Map<string, any>();

    customers.forEach((item: any) => {
      const key = `${item.customer_name}||${item.channel_type}`;
      if (!groupMap.has(key)) {
        const base: any = {
          customer_name: item.customer_name,
          channel_type: item.channel_type,
        };
        // initialize year columns to 0
        YEARS.forEach((y) => (base[y] = 0));
        groupMap.set(key, base);
      }

      const row = groupMap.get(key);
      const yearStr = String(item.year);
      if (YEARS.includes(yearStr)) {
        row[yearStr] = (row[yearStr] || 0) + (item.sumOfTotalSales || 0);
      }
    });

    return Array.from(groupMap.values()).map((row: any) => {
      const total = YEARS.reduce(
        (s: number, y: string) => s + (row[y] || 0),
        0,
      );
      return {
        ...row,
        [totalKey]: total,
      };
    });
  }

  // Read year columns from widget JSON headerColumns or explicit yearColumns config
  private getYearColumnsFromConfig(): string[] {
    // explicit list in widget config: data.yearColumns = ['2019','2020',...]
    if (this.data?.yearColumns && Array.isArray(this.data.yearColumns)) {
      return this.data.yearColumns.map((y: any) => String(y));
    }

    // derive from headerColumns keys if defined in widget JSON
    if (this.data?.headerColumns && Array.isArray(this.data.headerColumns)) {
      return this.data.headerColumns
        .map((c: any) => String(c.key))
        .filter((k: string) => /^\d{4}$/.test(k));
    }

    return [];
  }

  /**
   * Override the base processApiResponse so we can pivot the
   * customer data into quarterly subcolumns (year + quarter)
   * before the table header/rows are finalised.
   */

  // getGroupWidthPx(group: any): number {
  //   const cols: string[] = group?.columns || [];
  //   return cols.reduce((sum, c) => sum + (this.columnWidths[c] ?? this.DEFAULT_COL_WIDTH), 0);
  // }


  protected override processApiResponse(res: any): void {
    const widgetId = (this.data as any)?.widgetId || 'unknown';

    console.log(
      '[Custom GenericTableComponent] processApiResponse override - widgetId:',
      widgetId,
      'raw keys:',
      res ? Object.keys(res) : null,
      'enableQuarterlyPivot:',
      this.isQuarterlyEnabled(),
    );

    let tableData: any[] = [];
    let usedQuarterlyPivot = false;

    // Attempt quarterly pivot first when enabled and customer array present.
    if (this.isQuarterlyEnabled()) {
      const customerArray = this.findCustomerArray(res as any);
      console.log(
        '[Custom GenericTableComponent] processApiResponse - customerArray length:',
        customerArray.length,
      );

      if (
        customerArray &&
        customerArray.length > 0 &&
        this.hasQuarterField(customerArray)
      ) {
        const out = this.pivotCustomerQuarterlyData(customerArray);

        // Override headers/groups so the table renders quarters under each year.
        this.data = {
          ...this.data,
          headerColumns: out.headerColumns,
          headerGroups: out.headerGroups,
        };

        (this as any).displayedColumns = (this.data.headerColumns || []).map((c: any) => c.key);
        this.refreshStickyColumns();

        tableData = out.rows;
        usedQuarterlyPivot = true;

        console.log(
          '[Custom GenericTableComponent] processApiResponse - USING QUARTERLY PIVOT for widget:',
          widgetId,
          'headerColumns:',
          out.headerColumns,
          'headerGroups:',
          out.headerGroups,
          'sampleRow:',
          out.rows[0] || null,
        );
      } else {
        console.log(
          '[Custom GenericTableComponent] processApiResponse - quarterly pivot NOT applied (missing customer/quarter data) for widget:',
          widgetId,
        );
      }
    }

    // Fallback to original yearly/options logic when quarterly pivot isn't used.
    if (!usedQuarterlyPivot) {
      console.log(
        '[Custom GenericTableComponent] processApiResponse - falling back to base yearly/options logic for widget:',
        widgetId,
      );

      const eccArray = this.findEccArray(res as any);
      if (eccArray && eccArray.length > 0) {
        tableData = this.pivotEccData(eccArray);
      } else if (res?.customer) {
        const currentFilters = (this as any).globalFilterService.getCurrentFilters();
        const activeYearFilters = (Array.isArray(currentFilters?.requestParams?.year)
          ? currentFilters.requestParams.year
          : [currentFilters?.requestParams?.year]
        ).filter(Boolean).map(String);

        const map = new Map<string, any>();
        const yearsInData = new Set<string>();

        (res.customer as any[]).forEach((item: any) => {
          const key = `${item.customer_name}_${item.channel_type || ''}`;
          if (!map.has(key)) {
            map.set(key, {
              customer_name: item.customer_name,
              channel_type: item.channel_type,
              _total: 0,
            });
          }
          const row = map.get(key);

          if (item.year) {
            const yearStr = String(item.year);
            const matchesFilter = activeYearFilters.length === 0 || activeYearFilters.includes(yearStr);
            if (matchesFilter) {
              row[yearStr] = (this as any).formatCurrency(item.sumOfTotalSales);
              row._total += item.sumOfTotalSales || 0;
              yearsInData.add(yearStr);
            }
          }
        });

        tableData = Array.from(map.values()).map((row) => {
          row['TOTAL'] = (this as any).formatCurrency(row._total);
          delete row._total;
          return row;
        });

        // Update headerColumns to only show filtered years
        const idCols = [
          { key: 'customer_name', label: 'CUSTOMER NAME' },
          { key: 'channel_type', label: 'CHANNEL TYPE' }
        ];
        const yearCols = Array.from(yearsInData).sort().map(y => ({ key: y, label: y }));
        this.data = {
          ...this.data,
          headerColumns: [...idCols, ...yearCols, { key: 'TOTAL', label: 'TOTAL' }]
        };
      } else {
        tableData = res?.options || res?.data || res || [];
      }
    }

    this.isLoading = false;

    // Legacy/Specific Logic (copied from base)
    if (tableData.length > 0 && 'proposed_option_price' in tableData[0]) {
      tableData = tableData.map((row: any) => ({
        ...row,
        difference: Number(
          (row.proposed_option_price - row.current_option_price).toFixed(3),
        ),
      }));
      tableData.sort((a: any, b: any) => b.difference - a.difference);
    }

    (this as any).originalDataSource = tableData;
    (this as any).dataSource = [...(this as any).originalDataSource];

    if (!!this.data?.isPagination) {
      (this as any).pageIndex = 0; // Reset page on new data
      (this as any).setPagedData();
    }

    (this as any).displayedColumns =
      this.data?.headerColumns?.map((col: any) => col.key) || [];
    (this as any).columnLabels = Object.fromEntries(
      this.data?.headerColumns?.map((col: any) => [col.key, col.label]) || [],
    );
    this.refreshStickyColumns();

    this.initColumnWidths();

    console.log(
      '[Custom GenericTableComponent] processApiResponse - final displayedColumns for widget:',
      widgetId,
      (this as any).displayedColumns,
      'headerColumns:',
      this.data?.headerColumns,
      'headerGroups:',
      this.data?.headerGroups,
    );

    (this as any).initGroupedHeaders();

    setTimeout(() => {
      (this as any).paginatorComp?.updatePagination(
        (this as any).dataSource.length,
        (this as any).pageIndex,
      );
    }, 200);

    // Re-apply active legendSync filter if data was reloaded while a filter was active
    if ((this as any).activeLegendSyncFilter) {
      this.handleExternalLegendFilter((this as any).activeLegendSyncFilter);
    }

    // Apply column visibility based on active year filter in requestParams.
    // This handles the case where a chart legend click sets requestParams.year
    // but doesn't emit a legendSync event (only a regular filter update).
    const currentFilters = (this as any).globalFilterService.getCurrentFilters();
    const activeYearFilters = (Array.isArray(currentFilters?.requestParams?.year)
      ? currentFilters.requestParams.year
      : currentFilters?.requestParams?.year
        ? [currentFilters.requestParams.year]
        : []
    ).filter(Boolean).map(String);

    if (activeYearFilters.length > 0) {
      this.updateColumnVisibility(activeYearFilters);
    }
  }

  protected loadAndRenderChartWithPivot(filters?: any): any {
    const widgetId = (this.data as any)?.widgetId || 'unknown';
    const subscription: any = super.loadAndRenderChart(filters) as any;

    if (subscription && typeof subscription.subscribe === 'function') {
      return (subscription as any).pipe(
        tap((response: any) => {
          console.log(
            `${widgetId} [loadAndRenderChartWithPivot] raw API response:`,
            response,
          );
        }),
        map((response: any) => {
          const customerArray = this.findCustomerArray(response as any);
          console.log(
            `${widgetId} [loadAndRenderChartWithPivot] found customer array with ${customerArray.length} items`,
          );

          // if (customerArray && customerArray.length > 0) {
          //   const pivotedRows = this.pivotCustomerData(customerArray as any[]);
          //   console.log(
          //     `${widgetId} [loadAndRenderChartWithPivot] pivoted to ${pivotedRows.length} unique customer-channel combinations`,
          //   );
          //   if (pivotedRows.length > 0) {
          //     console.log(
          //       `${widgetId} [loadAndRenderChartWithPivot] sample pivoted row:`,
          //       pivotedRows[0],
          //     );
          //   }

          //   // Return the pivoted data in the same response structure
          //   // The parent component expects res?.options
          //   return {
          //     ...response,
          //     options: pivotedRows,
          //   };
          // }

          if (customerArray && customerArray.length > 0) {
            const quarterlyMode =
              this.isQuarterlyEnabled() && this.hasQuarterField(customerArray);
            console.log(
              `${widgetId} [loadAndRenderChartWithPivot] quarterlyMode:`,
              quarterlyMode,
            );

            if (quarterlyMode) {
              const out = this.pivotCustomerQuarterlyData(customerArray);

              // IMPORTANT: override headers/groups at runtime for this widget instance
              this.data = {
                ...this.data,
                headerColumns: out.headerColumns,
                headerGroups: out.headerGroups,
              };

              console.log(
                `${widgetId} [loadAndRenderChartWithPivot] using QUARTERLY pivot. headerColumns:`,
                out.headerColumns,
                'headerGroups:',
                out.headerGroups,
              );

              return { ...response, options: out.rows };
            }

            // fallback: yearly pivot (your existing logic)
            const pivotedRows = this.pivotCustomerData(customerArray as any[]);
            console.log(
              `${widgetId} [loadAndRenderChartWithPivot] using YEARLY pivot. headerColumns (from config or inferred):`,
              this.getYearColumnsFromConfig(),
              'sampleRow:',
              pivotedRows[0] || null,
            );
            return { ...response, options: pivotedRows };
          }

          console.log(
            `${widgetId} [loadAndRenderChartWithPivot] no customer array found, returning original response`,
          );
          return response;
        }),
      );
    }

    return subscription;
  }

  protected override initTableWithData(tableData: any): any {
    const widgetId = (this.data as any)?.widgetId || 'unknown';

    // Check if tableData has inline customer array
    if (tableData?.customer && Array.isArray(tableData.customer)) {
      const pivotedRows = this.pivotCustomerData(tableData.customer as any[]);
      console.log(
        `${widgetId} [initTableWithData] pivoted ${pivotedRows.length} rows from inline data`,
      );

      const result = super.initTableWithData({
        ...tableData,
        options: pivotedRows,
      });
      this.refreshStickyColumns();
      this.initColumnWidths();
      return result;
    }

    const result = super.initTableWithData(tableData);
    this.refreshStickyColumns();
    this.initColumnWidths();
    return result;
  }

  /**
   * Override base formatting to always display values in 'k' or 'm' units.
   * Examples: 900 -> 0.9k, 1500 -> 1.5k, 2500000 -> 2.5m
   */
  protected override formatCurrency(value: any): string {
    const num = Number(value);
    if (!isFinite(num) || isNaN(num)) return '-';

    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);

    if (abs >= 1_000_000) {
      const v = abs / 1_000_000;
      return sign + (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '') + 'M';
    }

    // Always use 'k' for thousands (including values < 1000)
    const vk = abs / 1_000;
    return sign + (Math.round(vk * 10) / 10).toFixed(1).replace(/\.0$/, '') + 'K';
  }

  public applySort(): void {
    if (!this.sortColumn) return;

    // Use (this as any) to access inherited dataSource property safely
    const dataArr = (this as any).dataSource as any[];
    if (!dataArr) return;

    dataArr.sort((a, b) => {
      let valA: number;
      let valB: number;

      if (this.sortType === 'group' && this.sortGroupColumns.length > 0) {
        valA = this.sortGroupColumns.reduce(
          (sum, col) => sum + this.parseValue(a[col]),
          0,
        );
        valB = this.sortGroupColumns.reduce(
          (sum, col) => sum + this.parseValue(b[col]),
          0,
        );
      } else {
        valA = this.parseValue(a[this.sortColumn!]);
        valB = this.parseValue(b[this.sortColumn!]);
      }

      const diff = valA - valB;
      if (diff === 0) return 0;
      return this.sortDirection === 'asc' ? diff : -diff;
    });

    // CRITICAL: Refresh the data source reference to trigger MatTable re-render
    (this as any).dataSource = [...dataArr];

    (this as any).pageIndex = 0;
    if (!!this.data?.isPagination) {
      (this as any).setPagedData();
    }
    setTimeout(() => {
      (this as any).paginatorComp?.updatePagination(
        (this as any).dataSource.length,
        (this as any).pageIndex,
      );
    }, 100);
  }

  private parseValue(val: any): number {
    if (val === null || val === undefined || val === '' || val === '-') {
      return this.sortDirection === 'desc' ? -Infinity : Infinity;
    }
    if (typeof val === 'number') return val;

    let str = String(val).replace(/,/g, '').trim();
    let multiplier = 1;
    const lastChar = str.charAt(str.length - 1).toUpperCase();
    if (lastChar === 'M') {
      multiplier = 1_000_000;
      str = str.slice(0, -1);
    } else if (lastChar === 'K') {
      multiplier = 1_000;
      str = str.slice(0, -1);
    } else if (lastChar === 'B') {
      multiplier = 1_000_000_000;
      str = str.slice(0, -1);
    }

    const num = parseFloat(str);
    return isNaN(num) ? 0 : num * multiplier;
  }

  // ─── Column Resize ────────────────────────────────────────────────────────

  // Helper: return true if any selected cell belongs to the provided row
  public isRowHighlighted(row: any): boolean {
    const selected = (this as any).selectedCells || [];
    if (!selected || selected.length === 0) return false;
    return selected.some((cell: any) => (this as any).isSameRow(cell.row, row));
  }

  // Helper: return true if any selected cell is in the provided column
  public isColumnHighlightedByCell(column: string): boolean {
    const selected = (this as any).selectedCells || [];
    if (!selected || selected.length === 0) return false;
    // Exclude identifier columns from column-level highlight
    const identifierKeys: string[] = Array.isArray(
      (this as any).data?.filterKey,
    )
      ? (this as any).data.filterKey
      : (this as any).data?.filterKey
        ? [(this as any).data.filterKey]
        : ['customer_name', 'channel_type'];

    if (
      identifierKeys.includes(column) ||
      ['customer_name', 'channel_type'].includes(column)
    ) {
      return false;
    }

    return selected.some((cell: any) => cell.column === column);
  }

  // Helper to detect identifier columns (customer_name / channel_type)
  public isIdentifierColumn(column: string): boolean {
    const identifierKeys: string[] = Array.isArray(
      (this as any).data?.filterKey,
    )
      ? (this as any).data.filterKey
      : (this as any).data?.filterKey
        ? [(this as any).data.filterKey]
        : ['customer_name', 'channel_type'];

    return (
      identifierKeys.includes(column) ||
      ['customer_name', 'channel_type'].includes(column)
    );
  }

  public getStickyColumns(): string[] {
    return this.stickyColumns;
  }

  public isStickyColumn(column: string): boolean {
    return this.getStickyColumns().includes(column);
  }

  public getStickyColumnLeft(column: string): number | null {
    const stickyColumns = this.getStickyColumns();
    if (!stickyColumns.includes(column)) {
      return null;
    }

    let left = 0;
    for (const stickyColumn of stickyColumns) {
      if (stickyColumn === column) {
        break;
      }
      left += this.columnWidths[stickyColumn] ?? this.DEFAULT_COL_WIDTH;
    }
    return left;
  }

  public getStickyColumnZIndex(
    column: string,
    isHeader: boolean = false,
  ): number | null {
    const stickyColumns = this.getStickyColumns();
    const index = stickyColumns.indexOf(column);
    if (index < 0) {
      return null;
    }

    // Use massive base z-index (100) to override any global or manual CSS class z-index (e.g. user added 8 to tableheader)
    const baseZIndex = isHeader ? 100 : 50;
    return baseZIndex + (stickyColumns.length - index);
  }

  // Keep header/column highlight in sync when a single cell is selected
  override onCellClick(row: any, column: string, event: MouseEvent): void {
    const cellIndex = (this as any).selectedCells.findIndex(
      (cell: any) => (this as any).isSameRow(cell.row, row) && cell.column === column,
    );
    const isAlreadySelected = cellIndex !== -1;

    const isMultiSelect = event.ctrlKey || event.metaKey;

    if (!isMultiSelect) {
      // Single select mode
      if (isAlreadySelected && (this as any).selectedCells.length === 1) {
        (this as any).selectedCells = [];
      } else {
        (this as any).selectedCells = [{ row, column }];
      }
    } else {
      // Multi select mode
      if (isAlreadySelected) {
        (this as any).selectedCells.splice(cellIndex, 1);
      } else {
        (this as any).selectedCells.push({ row, column });
      }
    }

    // Update filters based on current selection
    this.updateFilterFromSelectedCells();

    const selected = (this as any).selectedCells || [];
    if (selected.length === 1 && selected[0]?.column) {
      const col = selected[0].column as string;
      const identifierKeys: string[] = Array.isArray(
        (this as any).data?.filterKey,
      )
        ? (this as any).data.filterKey
        : (this as any).data?.filterKey
          ? [(this as any).data.filterKey]
          : ['customer_name', 'channel_type'];

      const isIdentifier =
        identifierKeys.includes(col) ||
        ['customer_name', 'channel_type'].includes(col);
      if (isIdentifier) {
        this.selectedColumnHeaders = new Set<string>();
      } else {
        this.selectedColumnHeaders = new Set<string>([col]);
      }
    } else {
      this.selectedColumnHeaders = new Set<string>();
    }
  }

  override onColumnHeaderClick(column: string, event: MouseEvent): void {
    if (this.suppressNextHeaderClick) {
      this.suppressNextHeaderClick = false;
      return;
    }

    const isMultiSelect = event.ctrlKey || event.metaKey;

    if (!isMultiSelect) {
      // Single select mode
      if (this.selectedColumnHeaders.has(column) && this.selectedColumnHeaders.size === 1) {
        this.selectedColumnHeaders.clear();
      } else {
        this.selectedColumnHeaders.clear();
        this.selectedColumnHeaders.add(column);
      }
    } else {
      // Multi select mode
      if (this.selectedColumnHeaders.has(column)) {
        this.selectedColumnHeaders.delete(column);
      } else {
        this.selectedColumnHeaders.add(column);
      }
    }

    // Dynamic Sort Integration
    if (this.sortColumn === column && this.sortType === 'column') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
      this.sortType = 'column';
      this.sortGroupColumns = [];
    }
    this.applySort();

    // Clear other types of selections to maintain clean logic
    (this as any).selectedCells = [];
    (this as any).selectedGroupHeaders.clear();

    // Emit filter update
    this.emitHeaderFilter();
  }

  override onGroupHeaderClick(
    group: { label: string; colspan: number; columns: string[] },
    event: MouseEvent,
  ): void {
    if (!group.label) return;

    const isMultiSelect = event.ctrlKey || event.metaKey;

    if (!isMultiSelect) {
      // Single select mode
      if (this.selectedGroupHeaders.has(group.label) && this.selectedGroupHeaders.size === 1) {
        this.selectedGroupHeaders.clear();
      } else {
        this.selectedGroupHeaders.clear();
        this.selectedGroupHeaders.add(group.label);
      }
    } else {
      // Multi select mode
      if (this.selectedGroupHeaders.has(group.label)) {
        this.selectedGroupHeaders.delete(group.label);
      } else {
        this.selectedGroupHeaders.add(group.label);
      }
    }

    // Dynamic Sort Integration for Year Groups
    if (this.sortColumn === group.label && this.sortType === 'group') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = group.label;
      this.sortDirection = 'desc';
      this.sortType = 'group';
      this.sortGroupColumns = group.columns || [];
    }
    this.applySort();

    // Clear other types of selections to maintain clean logic
    (this as any).selectedCells = [];
    (this as any).selectedColumnHeaders.clear();

    // Emit filter update
    this.emitHeaderFilter();
  }

  protected override emitHeaderFilter(): void {
    const data = this.data as any;
    const columnFilterKeyMap = data?.columnFilterKeyMap || {};
    const filterKey = data?.filterKey || [];
    const widgetId = data?.widgetId;
    const listener = data?.filterListener || data?.filterListner;
    const listenerStr = Array.isArray(listener)
      ? listener.join(',')
      : listener || '';

    const selfId = String(widgetId || '').trim();
    const selectionListenerParts = listenerStr
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => s && s !== selfId);
    const selectionListener = selectionListenerParts.length > 0 ? selectionListenerParts.join(',') : undefined;

    // If not quarterly and not having year mapping, fallback to parent
    if (!this.isQuarterlyEnabled() && !Object.values(columnFilterKeyMap).includes('year')) {
      super.emitHeaderFilter();
      return;
    }

    const selectedGroups: Set<string> =
      (this as any).selectedGroupHeaders || new Set<string>();
    const selectedColumns: Set<string> =
      (this as any).selectedColumnHeaders || new Set<string>();

    const filterValues: string[] = [];
    const yearValues: string[] = [];
    const qtrValues: string[] = [];

    const quarterlyMode = this.isQuarterlyEnabled();

    if (selectedGroups.size === 0 && selectedColumns.size === 0) {
      this.emitLegendFilter([]);
      const managedKeys = Array.isArray(filterKey) ? [...filterKey] : [filterKey];
      if (quarterlyMode && !managedKeys.includes('qtr')) {
        managedKeys.push('qtr');
      }

      managedKeys.forEach((key, index) => {
        const isLast = index === managedKeys.length - 1;
        if (isLast) {
          // Deselection: include self so table resets itself
          const listenerParts = listenerStr.split(',').map((s: string) => s.trim()).filter(Boolean);
          if (selfId && !listenerParts.includes(selfId)) {
            listenerParts.push(selfId);
          }
          const resetListener = listenerParts.length > 0 ? listenerParts.join(',') : undefined;

          this.globalFilterService.resetFilterSelection(key, false, resetListener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    selectedGroups.forEach((label: string) => {
      if (label && !filterValues.includes(label)) {
        filterValues.push(label);
      }
      if (/^\d{4}$/.test(label)) {
        yearValues.push(label);
      }
    });

    selectedColumns.forEach((column: string) => {
      const isYear = columnFilterKeyMap[column] === 'year' || /^\d{4}$/.test(column);
      if (isYear) {
        if (!yearValues.includes(column)) {
          yearValues.push(column);
        }
      }

      const quarterMatch = quarterlyMode && String(column).match(/^(\d{4})_(Q[1-4])$/i);
      if (quarterMatch) {
        const year = quarterMatch[1];
        const quarter = quarterMatch[2].toUpperCase();

        // Push values to maintain parallel arrays for "zipping" in CustomConfigService.
        // We push BOTH every time a coordinate is selected to keep the indices aligned.
        qtrValues.push(quarter);
        yearValues.push(year);
        return;
      }

      const label = ((this as any).columnLabels || {})[column] || column;
      if (label && !filterValues.includes(label)) {
        filterValues.push(label);
      }
    });

    // Handle Year and Qtr Selection explicitly via GlobalFilterService
    const updates = [];
    const managesYear = filterKey.includes('year') || Object.values(columnFilterKeyMap).includes('year');

    if (yearValues.length > 0 && managesYear) {
      updates.push({ key: 'year', values: yearValues });
    }
    if (qtrValues.length > 0 && quarterlyMode) {
      updates.push({ key: 'qtr', values: qtrValues });
    }

    updates.forEach((upd, idx) => {
      const isLast = idx === updates.length - 1;
      this.globalFilterService.updateFilterSelection(
        upd.key,
        upd.values,
        false,
        widgetId,
        isLast,
        selectionListener,
      );
    });

    // Only emit legend filter if we haven't already explicitly handled time-based components
    // via 'year' or 'quarter' keys. This prevents the base class from sending the entire
    // filterKey array as a redundant (and potentially mis-joined) filter.
    if (updates.length === 0) {
      this.emitLegendFilter(filterValues);
    } else {
      console.log('[Custom GenericTable] Skipping emitLegendFilter because explicit time-filters were sent:', updates);
    }
  }

  /**
   * Override base filter construction so that when a quarterly pivot cell
   * (e.g. "2024_Q1") is selected, the request payload includes both:
   *  - year: "2024"
   *  - qtr: "Q1"
   *
   * All other behaviour (identifiers, totals, non-quarter columns) remains
   * consistent with the parent implementation.
   */
  protected override updateFilterFromSelectedCells(): void {
    if (!(this as any).data?.filterKey) return;

    const data: any = (this as any).data;
    const columnFilterKeyMap: { [col: string]: string } =
      data.columnFilterKeyMap || {};
    const listener = data.filterListener || data.filterListner;
    const listenerStr = Array.isArray(listener)
      ? listener.join(',')
      : listener || '';
    const widgetId = data.widgetId;

    // All possible API keys this table manages (for cleanup)
    const allFilterKeys: string[] = Array.isArray(data.filterKey)
      ? [...data.filterKey]
      : [data.filterKey];

    // Ensure qtr is also cleaned up when using quarterly pivot, without
    // treating it as an identifier/row key.
    const quarterlyMode = this.isQuarterlyEnabled();
    if (quarterlyMode && !allFilterKeys.includes('qtr')) {
      allFilterKeys.push('qtr');
    }

    if ((this as any).selectedCells.length === 0) {
      // On DESELECTION: include self in listener so the table reloads itself
      // (same as stackbar legend: deselecting all legends reloads the chart)
      const listenerParts = listenerStr ? listenerStr.split(',').map((s: string) => s.trim()) : [];
      const selfId = String(widgetId || '').trim();
      if (selfId && !listenerParts.includes(selfId)) {
        listenerParts.push(selfId);
      }
      const resetListener = listenerParts.length > 0 ? listenerParts : undefined;

      // Clear all managed filter keys from requestParams
      allFilterKeys.forEach((key, index) => {
        const isLast = index === allFilterKeys.length - 1;
        if (isLast) {
          (this as any).globalFilterService.resetFilterSelection(key, false, resetListener);
        } else {
          (this as any).globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    // Build apiKey -> Set<value> or Array<value> from all selected cells
    const filterMap = new Map<string, any>();
    const identifierKeys: string[] = Array.isArray(data.filterKey)
      ? data.filterKey
      : [data.filterKey];
    // Respect configured total key (defaults to 'TOTAL') - do not include it as a filter attribute
    const totalKey = (data && data.totalKey) || 'TOTAL';

    for (const cell of (this as any).selectedCells) {
      const colKey = cell.column as string;
      const isTotalColumn =
        colKey === totalKey || columnFilterKeyMap[colKey] === totalKey;

      // Detect quarterly pivot column keys like "2024_Q1"
      const quarterMatch =
        quarterlyMode && /^(\d{4})_(Q[1-4])$/i.test(colKey)
          ? colKey.match(/^(\d{4})_(Q[1-4])$/i)
          : null;

      if (!isTotalColumn) {
        if (quarterMatch) {
          const yearPart = quarterMatch[1];
          const quarterPart = quarterMatch[2].toUpperCase();

          // Year filter from the column's year part - use parallel arrays for zipping
          if (!filterMap.has('year')) {
            filterMap.set('year', []);
          }
          if (Array.isArray(filterMap.get('year'))) {
            filterMap.get('year').push(yearPart);
          }

          // Quarter filter using backend attribute name "qtr" - use parallel arrays for zipping
          if (!filterMap.has('qtr')) {
            filterMap.set('qtr', []);
          }
          if (Array.isArray(filterMap.get('qtr'))) {
            filterMap.get('qtr').push(quarterPart);
          }
        } else {
          // Fallback to parent behaviour for non-quarter columns
          const apiKey = columnFilterKeyMap[colKey] || colKey;
          if (!filterMap.has(apiKey)) {
            filterMap.set(apiKey, new Set());
          }
          const val = apiKey !== colKey ? colKey : cell.row[colKey];
          if (val !== null && val !== undefined && val !== '') {
            const target = filterMap.get(apiKey);
            if (target instanceof Set) target.add(val);
          }
        }
      }

      // Also include identifier values from the selected row (e.g. customer_name, channel_type)
      // so that selecting a quarter cell will also pass row identifiers alongside year/qtr.
      for (const idKey of identifierKeys) {
        // qtr is not a row field; skip if present
        if (idKey === 'qtr') continue;

        const idVal = cell.row?.[idKey];
        if (idVal !== null && idVal !== undefined && idVal !== '') {
          if (!filterMap.has(idKey)) {
            filterMap.set(idKey, new Set());
          }
          const target = filterMap.get(idKey);
          if (target instanceof Set) target.add(idVal);
        }
      }
    }

    // Convert Sets to Arrays for emission
    const updates: any[] = [];
    filterMap.forEach((values, key) => {
      const valuesArray = values instanceof Set ? Array.from(values) : values;
      if (Array.isArray(valuesArray) && valuesArray.length > 0) {
        updates.push({ key, values: valuesArray });
      }
    });

    // Clear keys no longer active
    allFilterKeys.forEach((key) => {
      if (!filterMap.has(key)) {
        (this as any).globalFilterService.deleteKey(key, false);
      }
    });

    // Set each active key with its accumulated values
    // On SELECTION: exclude self from listener so the table does NOT call its own API.
    // (Same as stackbar legend: selecting a legend doesn't reload the chart itself.)
    const selfId = String(widgetId || '').trim();
    const selectionListenerParts = (listenerStr || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => s && s !== selfId);
    const selectionListener = selectionListenerParts.length > 0
      ? selectionListenerParts.join(',')
      : undefined;

    const activeKeys = Array.from(filterMap.keys());
    activeKeys.forEach((key, index) => {
      const values = Array.from(filterMap.get(key)!);
      const isLast = index === activeKeys.length - 1;
      (this as any).globalFilterService.updateFilterSelection(
        key,
        values,
        false,
        widgetId,
        isLast,
        selectionListener,
      );
    });
  }

  public override initColumnWidths(): void {
    if (!this.data) return;
    if (!(this.data as any).columnWidths) {
      (this.data as any).columnWidths = {};
    }

    const configWidths = (this.data as any).columnWidths;

    // Provide robust defaults for identifier columns to prevent "shrink" behavior
    if (configWidths['customer_name'] === undefined) {
      configWidths['customer_name'] = 350;
    }
    if (configWidths['channel_type'] === undefined) {
      configWidths['channel_type'] = 150;
    }

    this.columnWidths = {};
    const defaultWidth = (this as any).DEFAULT_COL_WIDTH ?? 150;

    (this.displayedColumns || []).forEach((col) => {
      this.columnWidths[col] = configWidths[col] ?? defaultWidth;
    });

    console.log('[Custom GenericTable] initColumnWidths - final columnWidths:', this.columnWidths);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy?.();
  }

  public downloadCsv(): void {
    if (!this.dataSource || !this.dataSource.length) return;

    const columns = this.displayedColumns || [];
    const labels = (this as any).columnLabels || {};

    const headers = columns.map((col: string) => labels[col] || col);
    const rows = this.dataSource.map((row: any) =>
      columns.map((col: string) => row[col] ?? '')
    );

    downloadAsExcel((this.data as any)?.widgetId || 'table', headers, rows);
  }
}
